import { useEffect, useMemo, useRef, useState } from 'react';
import type { Video } from '@/utils/video';

interface PlayerDemoProps {
  video: Video;
}

const parseDuration = (duration: string): number => {
  const parts = duration.split(':').map((value) => Number(value));
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
};

const formatClock = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default function PlayerDemo({ video }: PlayerDemoProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [tip, setTip] = useState('');
  const tipTimer = useRef<number | null>(null);

  const totalSeconds = useMemo(() => parseDuration(video.duration), [video.duration]);
  const currentSeconds = (totalSeconds * progress) / 100;

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setMuted(false);
  }, [video.id]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setPlaying(false);
          return 100;
        }
        return Math.min(100, prev + 100 / Math.max(totalSeconds, 1));
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, totalSeconds]);

  useEffect(() => () => {
    if (tipTimer.current) window.clearTimeout(tipTimer.current);
  }, []);

  const showTip = (message: string) => {
    setTip(message);
    if (tipTimer.current) window.clearTimeout(tipTimer.current);
    tipTimer.current = window.setTimeout(() => setTip(''), 2200);
  };

  const togglePlay = () => {
    setPlaying((prev) => {
      if (!prev) showTip('演示播放器：正在模拟播放');
      return !prev;
    });
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    setProgress(Math.min(100, Math.max(0, ratio * 100)));
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-black group">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} - 宅男视频`}
          className={`w-full h-full object-top object-cover transition-all duration-500 ${
            playing ? 'opacity-70 scale-105' : 'opacity-95'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30"></div>

        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-medium">
          <i className="ri-information-line"></i>
          演示播放器 · 无真实播放
        </span>

        <button
          type="button"
          aria-label={playing ? '暂停' : '播放'}
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
        >
          <span className={`flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105 ${playing ? 'opacity-0' : 'opacity-100'}`}>
            <i className="ri-play-fill text-4xl md:text-5xl"></i>
          </span>
        </button>

        <div className="absolute bottom-0 left-0 right-0 px-3 md:px-4 pb-3 md:pb-4 pt-8 bg-gradient-to-t from-black/80 to-transparent">
          <div
            className="group/seek w-full h-1.5 rounded-full bg-white/30 cursor-pointer"
            onClick={handleSeek}
            role="presentation"
          >
            <div className="relative h-full rounded-full bg-accent-500" style={{ width: `${progress}%` }}>
              <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-accent-400 opacity-0 group-hover/seek:opacity-100 transition-opacity"></span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 md:gap-4 text-background-50">
            <button type="button" aria-label="播放/暂停" onClick={togglePlay} className="cursor-pointer hover:text-accent-400 transition-colors">
              <i className={playing ? 'ri-pause-fill text-2xl' : 'ri-play-fill text-2xl'}></i>
            </button>
            <button
              type="button"
              aria-label="静音"
              onClick={() => {
                setMuted((prev) => !prev);
                showTip(muted ? '演示播放器：声音已开启（模拟）' : '演示播放器：已静音（模拟）');
              }}
              className="cursor-pointer hover:text-accent-400 transition-colors"
            >
              <i className={muted ? 'ri-volume-mute-line text-xl' : 'ri-volume-up-line text-xl'}></i>
            </button>
            <span className="text-xs tabular-nums text-background-50/90">
              {formatClock(currentSeconds)} / {video.duration}
            </span>
            <div className="ml-auto flex items-center gap-3">
              <button
                type="button"
                aria-label="设置"
                onClick={() => showTip('演示播放器：清晰度设置（模拟）')}
                className="cursor-pointer hover:text-accent-400 transition-colors"
              >
                <i className="ri-settings-3-line text-xl"></i>
              </button>
              <button
                type="button"
                aria-label="全屏"
                onClick={() => showTip('演示播放器：全屏功能（模拟）')}
                className="cursor-pointer hover:text-accent-400 transition-colors"
              >
                <i className="ri-fullscreen-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {tip && (
          <div className="absolute top-3 right-3 max-w-[70%] px-3 py-2 rounded-md bg-black/75 text-xs text-background-50 animate-fade-in">
            {tip}
          </div>
        )}
      </div>
      <p className="mt-3 text-xs text-foreground-500">
        提示：本页播放器为界面演示，播放、暂停、进度、音量等控件仅展示交互效果，不提供真实视频播放。
      </p>
    </div>
  );
}